import classNames from "classnames";
import React from "react";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/containers/MarkerMap";
import { formatDistance } from "tsi-common-react/src/apps/retail/helpers";
import {
    UncontrolledPopover,
    UncontrolledPopoverRef,
} from "tsi-common-react/src/common/Popover";
import { Trans } from "tsi-common-react/src/common/Trans";
import {
    ILocation,
    IRetailStoreWithDistance,
} from "tsi-common-react/src/models/location";
import { ISyncStoreID } from "tsi-common-react/src/models/nominals";
import { notEmpty } from "tsi-common-react/src/utils/functional";
import { t } from "ttag";

import fsMarker from "../../img/retail-locator/fs-marker.png";
import { FlagshipNearestStoresTile } from "../components/FlagshipNearestStoresTile";
import { LocatorSearch } from "../containers/LocatorSearch";
import { CMSFlagshipStorePage } from "../models";

const DEFAULT_MAP_CENTER = {
    lat: 37.0902,
    lng: -95.7129,
};
const DEFAULT_ZOOM = 4;

const MAP_STYLES = [
    {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ visibility: "simplified" }, { color: "#58585b" }],
    },
    {
        featureType: "landscape",
        elementType: "all",
        stylers: [{ visibility: "simplified" }, { color: "#f2f2f2" }],
    },
    {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "administrative.country",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "administrative.locality",
        stylers: [{ visibility: "off" }],
    },
];

const getFormattedDistInMiles = (
    stores: ReadonlyArray<IRetailStoreWithDistance>,
): string => {
    if (stores.length <= 0) {
        return t`Unknown`;
    }
    const formattedDist = formatDistance(stores[0].distance);
    return t`${formattedDist} miles.`;
};

const findCMSStore = (
    externalID: ISyncStoreID,
    storePages: CMSFlagshipStorePage[],
): CMSFlagshipStorePage | null => {
    const cmsStore = storePages.find((storePage) => {
        return storePage.meta.sync_id === externalID;
    });
    return cmsStore || null;
};

const createInfoWindowContent = (store: IRetailStoreWithDistance): string => {
    const url = `/locations/states/${store.state}/cities/${store.city}/stores/${store.id}/`;
    // TODO(i18n): Wrap location format in t tagged template to allow for localized city/state ordering: t`${store.city}, ${store.state}`
    return `<div className="loc__info-window">
            <a href="${url}">
                ${store.city}, ${store.state}
            </a>
        </div>`;
};

export const FlagshipNearestStores = (props: {
    stores: ReadonlyArray<IRetailStoreWithDistance>;
    storePages: CMSFlagshipStorePage[];
    maxTiles: number;
    preferredLocation: ILocation | null;
    includeLocationInfo: boolean;
    onAfterClose?: () => void;
    onAfterOpen?: () => void;
}): React.ReactNode => {
    const [focusedTileID, setFocusedTileID] =
        React.useState<ISyncStoreID | null>(null);
    const mapElem = React.useRef<MarkerMapHandle>(null);
    const popoverElem = React.useRef<UncontrolledPopoverRef>(null);

    const onBeforeInfoWindowOpen = (
        info: google.maps.InfoWindow,
        store: IRetailStoreWithDistance,
    ) => {
        const content = createInfoWindowContent(store);
        info.setContent(content);
        setFocusedTileID(store.external_id);
    };

    const onInfoWindowClose = () => {
        setFocusedTileID(null);
    };

    const locationLabel =
        props.preferredLocation?.formatted_address_long ||
        props.preferredLocation?.formatted_address ||
        t`you`;
    const distInMiles = getFormattedDistInMiles(props.stores);
    const modalClasses = classNames({
        "filters__dropdown filters__dropdown--location": true,
        "filters__dropdown--location-sm": true,
    });

    const storesIDs: Set<ISyncStoreID> = new Set();
    const tiles = props.stores
        // safety dedupe
        .reduce<IRetailStoreWithDistance[]>((memo, s) => {
            if (!storesIDs.has(s.external_id)) {
                storesIDs.add(s.external_id);
                memo.push(s);
            }
            return memo;
        }, [])
        .map((store) => {
            const storePage = findCMSStore(store.external_id, props.storePages);
            if (!storePage) {
                return null;
            }
            return (
                <FlagshipNearestStoresTile
                    key={`${store.external_id}`}
                    {...props}
                    store={store}
                    cmsProps={storePage}
                    isFocused={store.external_id === focusedTileID}
                    includeLocationInfo={props.includeLocationInfo}
                    onActivateMarker={() => {
                        mapElem.current?.onActivateMarker(store.external_id);
                    }}
                />
            );
        })
        .filter(notEmpty)
        .slice(0, props.maxTiles);

    return (
        <>
            <section className="filters">
                <p className="filters__filters filters__filters--small">
                    <Trans
                        fmtString={t`<Dark>Closest</Dark> Tempur-Pedic<Reg>®</Reg> Store to <Location></Location> is <Distance></Distance>`}
                        data={{
                            Dark: (content) => (
                                <strong key="strong">{content}</strong>
                            ),
                            Reg: (content) => <sup key="sup">{content}</sup>,
                            Location: () => (
                                <UncontrolledPopover
                                    key="location"
                                    ref={popoverElem}
                                    triggerContent={
                                        <>
                                            <span className="ada-screenreader-only">
                                                {t`Change your location to find retailers in that area.`}
                                            </span>
                                            {locationLabel}
                                        </>
                                    }
                                    triggerBtnProps={{
                                        className:
                                            "filters__filter origin-location al-store-locator__origin-location",
                                    }}
                                    modalProps={{
                                        contentLabel: locationLabel,
                                    }}
                                >
                                    <div className={modalClasses}>
                                        <LocatorSearch
                                            onSubmit={() => {
                                                if (popoverElem.current) {
                                                    popoverElem.current.requestClose();
                                                }
                                            }}
                                        />
                                    </div>
                                </UncontrolledPopover>
                            ),
                            Distance: () => (
                                <strong
                                    key="distance"
                                    className="bh-sl-nearest-results"
                                >
                                    {distInMiles}
                                </strong>
                            ),
                        }}
                    />
                </p>
            </section>
            <MarkerMap
                ref={mapElem}
                stores={props.stores}
                markerOptions={() => {
                    return {
                        icon: fsMarker,
                    };
                }}
                elemProps={{
                    "id": "locations__map",
                    "className": "locations__map",
                    "role": "region",
                    // TODO(i18n): Wrap aria-label in t tagged template: t`map`
                    "aria-label": "map",
                }}
                mapOptions={() => {
                    return {
                        center: DEFAULT_MAP_CENTER,
                        zoom: DEFAULT_ZOOM,
                        scrollwheel: false,
                        draggable: true,
                        disableDefaultUI: false,
                        clickableIcons: false,
                        mapTypeId: google.maps.MapTypeId.ROADMAP,
                        styles: MAP_STYLES,
                    };
                }}
                onBeforeInfoWindowOpen={onBeforeInfoWindowOpen}
                onInfoWindowClose={onInfoWindowClose}
            />
            <section
                className="locations__list-wrapper l-capped-width"
                role="region"
                // TODO(i18n): Wrap aria-label in t tagged template: t`Locations`
                aria-label="Locations"
            >
                <ul className="locations__list locations__list--index list">
                    {tiles}
                </ul>
            </section>
        </>
    );
};
